import { useState, useEffect, useRef } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Upload, FileText, X, Building2, Shield, CheckCircle2 } from "lucide-react";
import { provinciasEspana, listaProvincias } from "../data/provinciasEspana";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const RegistrationForm = () => {
  const initialFormData = {
    legalName: "",
    commercialName: "",
    nifCif: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [availableCities, setAvailableCities] = useState([]);
  const [showDocTermsModal, setShowDocTermsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Refs para scroll a campos con error
  const fieldRefs = {
    legalName: useRef(null),
    nifCif: useRef(null),
    phone: useRef(null),
    province: useRef(null),
    city: useRef(null),
    email: useRef(null),
    password: useRef(null),
    confirmPassword: useRef(null),
    companyRegistry: useRef(null),
    merchantRegistry: useRef(null),
    identityCard: useRef(null),
    documentTerms: useRef(null),
    termsConditions: useRef(null),
    privacyPolicy: useRef(null),
    turnstile: useRef(null),
  };

  const [documents, setDocuments] = useState({
    companyRegistry: null,
    merchantRegistry: null,
    identityCard: null,
  });

  const [consents, setConsents] = useState({
    documentTerms: false,
    termsConditions: false,
    privacyPolicy: false,
  });

  const [turnstileToken, setTurnstileToken] = useState(null);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedProvince("");
    setSelectedCity("");
    setDocuments({
      companyRegistry: null,
      merchantRegistry: null,
      identityCard: null,
    });
    setConsents({
      documentTerms: false,
      termsConditions: false,
      privacyPolicy: false,
    });
    setErrors({});
    setTurnstileToken(null);
    // Reset file inputs
    Object.values(fileInputRefs).forEach(ref => {
      if (ref.current) ref.current.value = '';
    });
    // Reset turnstile
    if (window.turnstile) {
      window.turnstile.reset();
    }
  };

  // Redirect to home
  const goToHome = () => {
    window.location.href = 'https://www.bluteca.com';
  };

  // Update cities when province changes
  useEffect(() => {
    if (selectedProvince && provinciasEspana[selectedProvince]) {
      setAvailableCities(provinciasEspana[selectedProvince]);
    } else {
      setAvailableCities([]);
    }
  }, [selectedProvince]);

  // Load Cloudflare Turnstile script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Define callback for turnstile
    window.onTurnstileSuccess = (token) => {
      setTurnstileToken(token);
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Refs per input file nascosti
  const fileInputRefs = {
    companyRegistry: useRef(null),
    merchantRegistry: useRef(null),
    identityCard: useRef(null),
  };

  const handleFileSelect = (docType) => {
    // Apri il file picker
    fileInputRefs[docType].current?.click();
  };

  const handleFileChange = (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Valida tipo file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos PDF, JPG o PNG");
      return;
    }

    // Valida dimensione (max 12MB)
    const maxSize = 12 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("El archivo es demasiado grande (máximo 12MB)");
      return;
    }

    // Formatta dimensione
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    
    setDocuments((prev) => ({
      ...prev,
      [docType]: { 
        file: file,
        name: file.name, 
        size: `${sizeInMB} MB` 
      },
    }));

    // Rimuovi errore se esisteva
    if (errors[docType]) {
      setErrors({ ...errors, [docType]: null });
    }
  };

  const removeFile = (docType) => {
    setDocuments((prev) => ({
      ...prev,
      [docType]: null,
    }));
    // Reset input file
    if (fileInputRefs[docType].current) {
      fileInputRefs[docType].current.value = '';
    }
  };

  // Validación de email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validación de teléfono (solo + y números)
  const isValidPhone = (phone) => {
    const phoneRegex = /^[+\d]+$/;
    return phoneRegex.test(phone);
  };

  // Sanitizzazione input - rimuove caratteri pericolosi per SQL/XSS injection
  const sanitizeInput = (value) => {
    if (typeof value !== 'string') return value;
    // Rimuove: < > " ' ` ; -- /* */ \ { } e caratteri di controllo
    return value
      .replace(/[<>"'`;\\{}]/g, '')
      .replace(/--/g, '')
      .replace(/\/\*/g, '')
      .replace(/\*\//g, '')
      .replace(/[\x00-\x1F\x7F]/g, '');
  };

  // Validazione NIF/CIF spagnolo (solo lettere e numeri)
  const sanitizeNifCif = (value) => {
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  };

  // Filtrar input de teléfono
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Solo permitir + y números
    const filteredValue = value.replace(/[^+\d]/g, '');
    setFormData({ ...formData, phone: filteredValue });
    if (errors.phone) {
      setErrors({ ...errors, phone: null });
    }
  };

  // Manejar cambios en inputs con sanitizzazione
  const handleInputChange = (field, value) => {
    let sanitizedValue = sanitizeInput(value);
    
    // Sanitizzazione specifica per NIF/CIF
    if (field === 'nifCif') {
      sanitizedValue = sanitizeNifCif(value);
    }
    
    // Email: solo caratteri validi
    if (field === 'email') {
      sanitizedValue = value.replace(/[^a-zA-Z0-9@._+-]/g, '');
    }
    
    // Codice postale: solo numeri
    if (field === 'postalCode') {
      sanitizedValue = value.replace(/[^\d]/g, '').slice(0, 5);
    }
    
    setFormData({ ...formData, [field]: sanitizedValue });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  // Manejar cambios en checkboxes
  const handleConsentChange = (field, checked) => {
    setConsents({ ...consents, [field]: checked });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  // Scroll al campo con error
  const scrollToField = (fieldName) => {
    if (fieldRefs[fieldName]?.current) {
      fieldRefs[fieldName].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldRefs[fieldName].current.focus?.();
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Campos obligatorios de datos empresa
    if (!formData.legalName.trim()) {
      newErrors.legalName = "Nombre Legal es obligatorio";
    }
    if (!formData.nifCif.trim()) {
      newErrors.nifCif = "NIF/CIF es obligatorio";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Teléfono es obligatorio";
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = "Teléfono solo puede contener + y números";
    }
    if (!selectedProvince) {
      newErrors.province = "Provincia es obligatoria";
    }
    if (!selectedCity) {
      newErrors.city = "Ciudad es obligatoria";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email es obligatorio";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Email no es válido";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Contraseña es obligatoria";
    } else if (formData.password.length < 6) {
      newErrors.password = "Contraseña debe tener al menos 6 caracteres";
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirmar contraseña es obligatorio";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    // Documentos obligatorios
    if (!documents.companyRegistry) {
      newErrors.companyRegistry = "Tarjeta CIF definitivo es obligatorio";
    }
    if (!documents.merchantRegistry) {
      newErrors.merchantRegistry = "Modelo 036/037 es obligatorio";
    }
    if (!documents.identityCard) {
      newErrors.identityCard = "DNI del representante legal es obligatorio";
    }

    // Consentimientos obligatorios
    if (!consents.documentTerms) {
      newErrors.documentTerms = "Debe aceptar los términos para cargar documentos";
    }
    if (!consents.termsConditions) {
      newErrors.termsConditions = "Debe aceptar los términos y condiciones";
    }
    if (!consents.privacyPolicy) {
      newErrors.privacyPolicy = "Debe aceptar la política de privacidad";
    }

    // Turnstile
    if (!turnstileToken) {
      newErrors.turnstile = "Debe completar la verificación de seguridad";
    }

    return newErrors;
  };

  // Manejar submit
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Obtener el primer error y mostrar toast
      const firstErrorKey = Object.keys(validationErrors)[0];
      const firstErrorMessage = validationErrors[firstErrorKey];
      
      toast.error(firstErrorMessage, {
        duration: 4000,
      });

      // Scroll al primer campo con error
      scrollToField(firstErrorKey);
      return;
    }

    // Preparar FormData per invio
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Dati azienda
      formDataToSend.append('legal_name', formData.legalName);
      formDataToSend.append('commercial_name', formData.commercialName || '');
      formDataToSend.append('nif_cif', formData.nifCif);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('province', selectedProvince);
      formDataToSend.append('city', selectedCity);
      formDataToSend.append('postal_code', formData.postalCode || '');
      formDataToSend.append('address', formData.address || '');
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      
      // Consensi
      formDataToSend.append('consent_document_terms', consents.documentTerms);
      formDataToSend.append('consent_terms_conditions', consents.termsConditions);
      formDataToSend.append('consent_privacy_policy', consents.privacyPolicy);
      
      // Turnstile
      formDataToSend.append('turnstile_token', turnstileToken);
      
      // Documenti
      formDataToSend.append('doc_cif', documents.companyRegistry.file);
      formDataToSend.append('doc_modelo', documents.merchantRegistry.file);
      formDataToSend.append('doc_dni', documents.identityCard.file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/register`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Error en el registro');
      }

      // Mostra modal di successo
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || "Error al procesar el registro. Inténtelo de nuevo.", {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
      // Reset Turnstile per permettere un nuovo tentativo
      setTurnstileToken(null);
      if (window.turnstile) {
        window.turnstile.reset();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_altas-demo/artifacts/tufzwqkc_bluteca_logo_no_bg.png" 
              alt="Bluteca Logo" 
              width="287"
              height="128"
              style={{ width: '200px', height: 'auto' }}
            />
          </div>
          <div className="text-center">
            <h1 className="text-lg font-semibold text-slate-800">
              Solicitud de Acceso a la Demo
            </h1>
            <p className="text-orange-500 text-xs font-medium">
              Introducir datos exactos: nombre legal, NIF y email no serán modificables
            </p>
            <p className="text-slate-500 text-xs">
              ¡No te preocupes, jamás vamos a ceder o transmitir tus datos!
            </p>
          </div>
          <a
            href="https://www.bluteca.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Volver a bluteca.com
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="p-8">
            {/* Grid Layout - 3 Columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
              
              {/* Column 1: Company Data */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-800">Datos de la Empresa</h3>
                </div>

                <div ref={fieldRefs.legalName}>
                  <Label htmlFor="legalName" className="text-sm font-medium text-slate-700">
                    Nombre Legal de la Empresa <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="legalName"
                    placeholder="Hipotecas SL"
                    className={`mt-1.5 ${errors.legalName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    data-testid="input-legal-name"
                    tabIndex={1}
                    value={formData.legalName}
                    onChange={(e) => handleInputChange('legalName', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="commercialName" className="text-sm font-medium text-slate-700">
                    Nombre Comercial <span className="text-slate-400">(opcional)</span>
                  </Label>
                  <Input
                    id="commercialName"
                    placeholder="Tu Hipoteca"
                    className="mt-1.5"
                    data-testid="input-commercial-name"
                    tabIndex={2}
                    value={formData.commercialName}
                    onChange={(e) => handleInputChange('commercialName', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div ref={fieldRefs.nifCif}>
                    <Label htmlFor="nifCif" className="text-sm font-medium text-slate-700">
                      NIF/CIF <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nifCif"
                      placeholder="ES12345678"
                      className={`mt-1.5 ${errors.nifCif ? 'border-red-500 focus:ring-red-500' : ''}`}
                      data-testid="input-nif-cif"
                      tabIndex={3}
                      value={formData.nifCif}
                      onChange={(e) => handleInputChange('nifCif', e.target.value)}
                    />
                  </div>
                  <div ref={fieldRefs.phone}>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Teléfono <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      placeholder="+34911234567"
                      className={`mt-1.5 ${errors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                      data-testid="input-phone"
                      tabIndex={4}
                      value={formData.phone}
                      onChange={handlePhoneChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div ref={fieldRefs.province}>
                    <Label htmlFor="province" className="text-sm font-medium text-slate-700">
                      Provincia <span className="text-red-500">*</span>
                    </Label>
                    <Select onValueChange={(value) => { setSelectedProvince(value); setSelectedCity(""); if(errors.province) setErrors({...errors, province: null}); }}>
                      <SelectTrigger className={`mt-1.5 ${errors.province ? 'border-red-500' : ''}`} data-testid="select-province" tabIndex={5}>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {listaProvincias.map((provincia) => (
                          <SelectItem key={provincia} value={provincia}>
                            {provincia}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div ref={fieldRefs.city}>
                    <Label htmlFor="city" className="text-sm font-medium text-slate-700">
                      Ciudad <span className="text-red-500">*</span>
                    </Label>
                    <Select disabled={!selectedProvince} onValueChange={(value) => { setSelectedCity(value); if(errors.city) setErrors({...errors, city: null}); }}>
                      <SelectTrigger className={`mt-1.5 ${errors.city ? 'border-red-500' : ''}`} data-testid="select-city" tabIndex={6}>
                        <SelectValue placeholder={selectedProvince ? "Seleccionar" : "Primero provincia"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map((ciudad) => (
                          <SelectItem key={ciudad} value={ciudad}>
                            {ciudad}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="postalCode" className="text-sm font-medium text-slate-700">
                      C. Postal
                    </Label>
                    <Input
                      id="postalCode"
                      placeholder="28001"
                      className="mt-1.5"
                      data-testid="input-postal-code"
                      tabIndex={7}
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm font-medium text-slate-700">
                    Dirección
                  </Label>
                  <Input
                    id="address"
                    placeholder="Calle Mayor 123"
                    className="mt-1.5"
                    data-testid="input-address"
                    tabIndex={8}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                  />
                </div>

                <div ref={fieldRefs.email}>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Correo electrónico <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="empresa@ejemplo.com"
                    className={`mt-1.5 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                    data-testid="input-email"
                    tabIndex={9}
                    autoComplete="new-email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div ref={fieldRefs.password}>
                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                      Contraseña <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className={`mt-1.5 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                      data-testid="input-password"
                      tabIndex={10}
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                    />
                  </div>
                  <div ref={fieldRefs.confirmPassword}>
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                      Confirmar <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className={`mt-1.5 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                      data-testid="input-confirm-password"
                      tabIndex={11}
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Column 2: Documents */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-800">Documentos Requeridos</h3>
                </div>

                {/* Document 1 - Company Registry */}
                <div ref={fieldRefs.companyRegistry}>
                  <Label className="text-sm font-medium text-slate-700">
                    Tarjeta CIF definitivo <span className="text-red-500">*</span>
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRefs.companyRegistry}
                    onChange={(e) => handleFileChange("companyRegistry", e)}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  {!documents.companyRegistry ? (
                    <div
                      onClick={() => handleFileSelect("companyRegistry")}
                      className={`mt-1.5 upload-zone rounded-lg p-6 text-center cursor-pointer ${errors.companyRegistry ? 'border-red-500' : ''}`}
                      data-testid="upload-company-registry"
                      tabIndex={12}
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Haga clic para cargar</p>
                      <p className="text-xs text-slate-400 mt-1">JPEG, PNG, PDF (máx 12MB)</p>
                    </div>
                  ) : (
                    <div className="mt-1.5 upload-zone has-file rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{documents.companyRegistry.name}</p>
                          <p className="text-xs text-slate-500">{documents.companyRegistry.size}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile("companyRegistry")}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Document 2 - Merchant Registry */}
                <div ref={fieldRefs.merchantRegistry}>
                  <Label className="text-sm font-medium text-slate-700">
                    Modelo 036 / 037 (declaración censal) para el CNAE <span className="text-red-500">*</span> <span className="text-slate-400">(hipotecas CNAE 6492, Consultores CNAE 6619)</span>
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRefs.merchantRegistry}
                    onChange={(e) => handleFileChange("merchantRegistry", e)}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  {!documents.merchantRegistry ? (
                    <div
                      onClick={() => handleFileSelect("merchantRegistry")}
                      className={`mt-1.5 upload-zone rounded-lg p-6 text-center cursor-pointer ${errors.merchantRegistry ? 'border-red-500' : ''}`}
                      data-testid="upload-merchant-registry"
                      tabIndex={13}
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Haga clic para cargar</p>
                      <p className="text-xs text-slate-400 mt-1">JPEG, PNG, PDF (máx 12MB)</p>
                    </div>
                  ) : (
                    <div className="mt-1.5 upload-zone has-file rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{documents.merchantRegistry.name}</p>
                          <p className="text-xs text-slate-500">{documents.merchantRegistry.size}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile("merchantRegistry")}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Document 3 - Identity Card */}
                <div ref={fieldRefs.identityCard}>
                  <Label className="text-sm font-medium text-slate-700">
                    Dni del representante legal <span className="text-red-500">*</span>
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRefs.identityCard}
                    onChange={(e) => handleFileChange("identityCard", e)}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  {!documents.identityCard ? (
                    <div
                      onClick={() => handleFileSelect("identityCard")}
                      className={`mt-1.5 upload-zone rounded-lg p-6 text-center cursor-pointer ${errors.identityCard ? 'border-red-500' : ''}`}
                      data-testid="upload-identity-card"
                      tabIndex={14}
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Haga clic para cargar</p>
                      <p className="text-xs text-slate-400 mt-1">JPEG, PNG, PDF (máx 12MB)</p>
                    </div>
                  ) : (
                    <div className="mt-1.5 upload-zone has-file rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{documents.identityCard.name}</p>
                          <p className="text-xs text-slate-500">{documents.identityCard.size}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile("identityCard")}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Column 3: Consents */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-800">Consentimientos</h3>
                </div>

                <div className="space-y-4">
                  <div ref={fieldRefs.documentTerms} className={`flex items-start gap-3 ${errors.documentTerms ? 'p-2 rounded border border-red-500 bg-red-50' : ''}`}>
                    <Checkbox
                      id="documentTerms"
                      checked={consents.documentTerms}
                      onCheckedChange={(checked) => handleConsentChange('documentTerms', checked)}
                      className="mt-0.5"
                      data-testid="checkbox-document-terms"
                      tabIndex={15}
                    />
                    <Label htmlFor="documentTerms" className="text-sm text-slate-600 cursor-pointer">
                      He leído y acepto los{" "}
                      <button 
                        type="button"
                        onClick={(e) => { e.preventDefault(); setShowDocTermsModal(true); }}
                        className="text-orange-500 hover:underline"
                      >
                        términos para cargar documentos
                      </button>
                      <span className="text-red-500">*</span>
                    </Label>
                  </div>

                  <div ref={fieldRefs.termsConditions} className={`flex items-start gap-3 ${errors.termsConditions ? 'p-2 rounded border border-red-500 bg-red-50' : ''}`}>
                    <Checkbox
                      id="termsConditions"
                      className="mt-0.5"
                      data-testid="checkbox-terms-conditions"
                      tabIndex={16}
                      checked={consents.termsConditions}
                      onCheckedChange={(checked) => handleConsentChange('termsConditions', checked)}
                    />
                    <Label htmlFor="termsConditions" className="text-sm text-slate-600 cursor-pointer">
                      He leído y acepto los{" "}
                      <a href="https://bluteca.com/es/terminos-condiciones/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">
                        términos y condiciones
                      </a><span className="text-red-500">*</span>
                    </Label>
                  </div>

                  <div ref={fieldRefs.privacyPolicy} className={`flex items-start gap-3 ${errors.privacyPolicy ? 'p-2 rounded border border-red-500 bg-red-50' : ''}`}>
                    <Checkbox
                      id="privacyPolicy"
                      className="mt-0.5"
                      data-testid="checkbox-privacy-policy"
                      tabIndex={17}
                      checked={consents.privacyPolicy}
                      onCheckedChange={(checked) => handleConsentChange('privacyPolicy', checked)}
                    />
                    <Label htmlFor="privacyPolicy" className="text-sm text-slate-600 cursor-pointer">
                      He leído y acepto la{" "}
                      <a 
                        href="https://bluteca.com/es/politica-privacidad/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:underline"
                      >
                        política de privacidad
                      </a><span className="text-red-500">*</span>
                    </Label>
                  </div>

                </div>

                {/* Important Note */}
                <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-700 mb-1">Nota importante:</p>
                  <p className="text-sm text-slate-600">
                    Una vez envíe este formulario, recibirá un correo electrónico para validar su dirección de email. Tras completar este paso, nuestro equipo revisará su solicitud y, en breve, recibirá un email de confirmación.
                  </p>
                  <p className="text-sm text-slate-600 mt-2">
                    A partir de ese momento, podrá comenzar a disfrutar del programa de forma totalmente gratuita durante 15 días.
                  </p>
                </div>

                {/* Cloudflare Turnstile Captcha */}
                <div ref={fieldRefs.turnstile} className={`mt-4 ${errors.turnstile ? 'p-2 rounded border border-red-500' : ''}`}>
                  <div 
                    className="cf-turnstile" 
                    data-sitekey="0x4AAAAAACw68zzDC5Ounnoh"
                    data-callback="onTurnstileSuccess"
                    data-theme="light"
                  ></div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 text-lg rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-submit-registration"
                  tabIndex={18}
                >
                  {isSubmitting ? "Procesando..." : "Registrarse"}
                </Button>

                {/* Reset and Home buttons */}
                <div className="flex gap-3 mt-4">
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                    className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-50"
                  >
                    Limpiar formulario
                  </Button>
                  <Button
                    type="button"
                    onClick={goToHome}
                    variant="outline"
                    className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    Volver a bluteca.com
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-slate-500">
          <p>© 2026 Bluteca by <a href="https://bluplanetweb.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline">Bluplanet Web S.L.</a> Todos los derechos reservados.</p>
        </footer>
      </main>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-green-600 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-6 h-6" />
              ¡Registro completado!
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4 text-sm text-slate-600">
            <p>
              Su solicitud ha sido recibida correctamente.
            </p>
            <p>
              Por favor, revise su correo electrónico y haga clic en el enlace de verificación para confirmar su dirección de email.
            </p>
            <p className="text-slate-500">
              Una vez verificado, nuestro equipo revisará su solicitud y le notificará por email.
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button 
              onClick={goToHome}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Terms Modal */}
      <Dialog open={showDocTermsModal} onOpenChange={setShowDocTermsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-800">
              Protección de datos
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4 text-sm text-slate-600">
            <p>
              Al utilizar la opción adicional y voluntaria de aportar documentación acreditativa (CIF definitivo, Modelo 036/037 —declaración censal— y DNI del representante legal), usted otorga su consentimiento expreso para que dichos datos sean recogidos, tratados y utilizados por nuestra entidad con la finalidad de gestionar la formalización y ejecución de la relación contractual.
            </p>
            <p>
              Asimismo, el uso del servicio se regirá por lo dispuesto en nuestra Política de Protección de Datos, que resultará de aplicación en todo momento.
            </p>
          </div>
          <div className="mt-6 flex justify-end">
            <Button 
              onClick={() => setShowDocTermsModal(false)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationForm;
